import React from 'react';
import { Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { AppButton } from '../common/AppButton';
import { AppFilterChips } from '../common/AppFilterChips';
import { ExpansionFilterSelect } from '../common/ExpansionFilterSelect';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { FONT_SIZE, RADIUS, SPACING } from '../../constants/constants';
import type { FilterOption } from '../../hooks/UseCardView';
import type { CardType } from '../../types/card';
import { DECISION_KIND_LABEL, type DecisionKind } from '../../utils/decidedPrice';

// ─── 판매가 결정 — 위쪽 필터 · 일괄 가격 줄(감사 card-controltower#341) ────────────────────────────────────────
//
// 확장팩 · 검색 · 보기(미설정만 · 즐겨찾기) · 레어도 · 구분(일반 · 특일 · 미러) + 일괄 가격 [적용] · 네이버 가격 미만만 적용.
// 값은 화면(DecidedPricePage)이 가진다 — 바꾸면 화면이 선택 · 입력을 비운다(#330 규칙).

const KIND_OPTIONS: FilterOption[] = (Object.keys(DECISION_KIND_LABEL) as DecisionKind[])
  .map((k) => ({ value: k, label: DECISION_KIND_LABEL[k] }));

/** 게임 칩(감사 card-controltower#438) — 확장팩 앞에 둔다. 'ALL' = 전체. */
export const DECIDED_GAME_OPTIONS: FilterOption[] = [
  { value: 'POKEMON', label: '포켓몬' },
  { value: 'ONE_PIECE', label: '원피스' },
  { value: 'DIGIMON', label: '디지몬' },
];

export interface DecidedPriceFilterBarProps {
  phone: boolean;
  /** 고른 게임(CardType) — 'ALL' 이면 전체. */
  game: string;
  onGame: (v: string) => void;
  expansion: string;
  expansionOptions: FilterOption[];
  onExpansion: (v: string) => void;
  search: string;
  onSearch: (v: string) => void;
  unsetOnly: boolean;
  favOnly: boolean;
  favoriteCount: number;
  onView: (next: { unsetOnly: boolean; favOnly: boolean }) => void;
  /** 고른 레어도(여럿 — OR, 감사 #448). 비면 전체. */
  rarities: string[];
  rarityOptions: FilterOption[];
  onRarities: (v: string[]) => void;
  kinds: DecisionKind[];
  onKinds: (v: DecisionKind[]) => void;
  // 일괄
  bulkPrice: string;
  onBulkPrice: (v: string) => void;
  belowOnly: boolean;
  onBelowOnly: (v: boolean) => void;
  selectedCount: number;
  bulkDisabledReason?: string;
  bulkRunning: boolean;
  onBulkApply: () => void;
}

export const DecidedPriceFilterBar: React.FC<DecidedPriceFilterBarProps> = ({
  phone, game, onGame, expansion, expansionOptions, onExpansion, search, onSearch,
  unsetOnly, favOnly, favoriteCount, onView, rarities, rarityOptions, onRarities, kinds, onKinds,
  bulkPrice, onBulkPrice, belowOnly, onBelowOnly, selectedCount, bulkDisabledReason, bulkRunning, onBulkApply,
}) => {
  const t = useThemeTokens();
  const ctrlHeight = phone ? 44 : 32;

  const viewValue = [unsetOnly ? 'unset' : null, favOnly ? 'fav' : null].filter((v): v is string => v != null);
  const viewOptions: FilterOption[] = [
    { value: 'unset', label: '미설정만' },
    { value: 'fav', label: `즐겨찾기 ${favoriteCount}` },
  ];

  return (
    <div
      data-testid="decided-filter-bar"
      style={{
        display: 'flex', flexDirection: 'column', gap: SPACING.xs, padding: `${SPACING.xs}px ${SPACING.sm}px`,
        background: t.bgSurface, border: `1px solid ${t.border}`, borderRadius: RADIUS.lg,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs, flexWrap: 'wrap' }}>
        <AppFilterChips
          label="게임"
          options={[{ value: 'ALL', label: '전체' }, ...DECIDED_GAME_OPTIONS]}
          value={[game]}
          // 하나만 켠다 — 켜진 칩을 다시 누르면 전체.
          onChange={(next) => onGame(next.find((v) => v !== game) ?? 'ALL')}
        />
        <ExpansionFilterSelect
          value={expansion}
          onChange={onExpansion}
          options={expansionOptions}
          gameType={game as CardType | 'ALL'}
          style={{ width: phone ? '100%' : 240 }}
        />
        <Input
          allowClear
          aria-label="카드 검색"
          prefix={<SearchOutlined style={{ color: t.textMuted }} />}
          placeholder="카드명 · 상품코드 검색"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          style={{ width: phone ? '100%' : 220, height: ctrlHeight }}
        />
        <AppFilterChips
          label="보기"
          options={viewOptions}
          value={viewValue}
          onChange={(next) => onView({ unsetOnly: next.includes('unset'), favOnly: next.includes('fav') })}
        />
        <div style={{
          display: 'flex', alignItems: 'center', gap: SPACING.xs, flexWrap: 'wrap',
          marginLeft: phone ? 0 : 'auto', width: phone ? '100%' : undefined,
        }}>
          <span style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, whiteSpace: 'nowrap' }}>선택 {selectedCount}건</span>
          <Input
            aria-label="일괄 가격"
            inputMode="numeric"
            placeholder="일괄 가격"
            value={bulkPrice}
            disabled={bulkRunning}
            onChange={(e) => onBulkPrice(e.target.value)}
            onPressEnter={() => { if (!bulkDisabledReason) onBulkApply(); }}
            style={{ width: phone ? 120 : 110, height: ctrlHeight, textAlign: 'right' }}
          />
          <AppButton
            variant="primary"
            size={phone ? 'lg' : 'md'}
            loading={bulkRunning}
            disabled={!!bulkDisabledReason}
            disabledReason={bulkDisabledReason}
            onClick={onBulkApply}
          >
            적용
          </AppButton>
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: SPACING.xxs, fontSize: FONT_SIZE.xs, color: t.textSecondary,
            cursor: 'pointer', minHeight: ctrlHeight, whiteSpace: 'nowrap',
          }}>
            <input
              type="checkbox"
              checked={belowOnly}
              disabled={bulkRunning}
              onChange={(e) => onBelowOnly(e.target.checked)}
              style={{ width: 16, height: 16, accentColor: t.accent }}
            />
            네이버 가격 미만만 적용
          </label>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.md, flexWrap: 'wrap' }}>
        <AppFilterChips
          // 여러 개 고를 수 있다(OR, 감사 #448) — '전체' 로 비우고, 고른 수를 라벨에 보인다.
          label={rarities.length > 0 ? `레어도 ${rarities.length}개` : '레어도'}
          options={[{ value: '__ALL__', label: '전체' }, ...rarityOptions]}
          value={rarities.length === 0 ? ['__ALL__'] : rarities}
          onChange={(next) => {
            if (next.includes('__ALL__') && rarities.length > 0) { onRarities([]); return; }
            onRarities(next.filter((v) => v !== '__ALL__'));
          }}
        />
        <AppFilterChips
          label="구분"
          options={KIND_OPTIONS}
          value={kinds}
          onChange={(next) => onKinds(next as DecisionKind[])}
        />
      </div>
    </div>
  );
};
